/**
 * _angles.jsx — Shared A/B angle helpers used across the Demo
 * Director Insights tab (single ReengagePanel, BatchReengageView,
 * SubjectLineExperimentsPanel).
 *
 * Owns:
 *   • ANGLE_DEFAULTS — hardcoded labels for instant first-paint
 *   • useAnglePresets() — hydrates labels from backend catalog
 *   • useAngleRecommendation(industryPreset) — auto-recommend hook
 *   • AnglePicker — the styled <select> used in both flows
 *   • RecommendedPill — Recommended/Tentative/Suggested pill +
 *     confidence tooltip
 *   • variantBadgeCls — variant A/B badge tailwind class helper
 */
import React, { useState } from 'react';
import { Sparkles } from 'lucide-react';
import api from '../../../lib/api';

// Five canonical angles — keys must match
// `services/reengage_drafter.py::ANGLE_PRESETS`. Hardcoded so the
// picker is interactive on first paint; `useAnglePresets` then
// hydrates the labels from the backend so any rename surfaces
// without a frontend redeploy.
export const ANGLE_DEFAULTS = [
  { key: 'curiosity', label: 'Curiosity' },
  { key: 'roi', label: 'ROI' },
  { key: 'urgency', label: 'Urgency' },
  { key: 'social_proof', label: 'Social Proof' },
  { key: 'pain_point', label: 'Pain-Point' },
];

export const useAnglePresets = () => {
  const [angles, setAngles] = useState(ANGLE_DEFAULTS);
  React.useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const res = await api.get('/admin/demo-director/angle-presets');
        if (cancelled) return;
        const next = (res.data?.angles || []).map(
          (a) => ({ key: a.key, label: a.label }),
        );
        if (next.length > 0) setAngles(next);
      } catch (_e) { /* fall back to hardcoded defaults */ }
    };
    run();
    return () => { cancelled = true; };
  }, []);
  return angles;
};

export const AnglePicker = ({ value, onChange, angles, label, disabled, testId }) => (
  <label className="flex items-center gap-1 text-[10px] font-semibold text-slate-600">
    <span className="uppercase tracking-wider text-slate-500">{label}</span>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="text-[11px] border border-slate-200 rounded px-1.5 py-0.5 bg-white hover:border-rose-300 focus:outline-none focus:ring-1 focus:ring-rose-300 disabled:opacity-50"
      data-testid={testId}
    >
      {angles.map((a) => (
        <option key={a.key} value={a.key}>{a.label}</option>
      ))}
    </select>
  </label>
);

export const variantBadgeCls = (variant) => (
  variant === 'A'
    ? 'bg-violet-100 text-violet-700 ring-violet-200'
    : 'bg-teal-100 text-teal-700 ring-teal-200'
);

/**
 * Auto-recommend angles for the A/B drafter. Pulls from
 * `/admin/demo-director/angle-recommendation`. Industry-scoped when
 * the industry has ≥3 sends; falls back to global when total
 * named-angle sends ≥ 5; else returns `available=false` so the UI
 * sticks with the curiosity/roi default and hides the Recommended
 * pill. The hook is fire-and-forget — never blocks the modal render.
 */
export const useAngleRecommendation = (industryPreset) => {
  const [rec, setRec] = useState(null);
  React.useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const qs = industryPreset ? `?industry_preset=${encodeURIComponent(industryPreset)}` : '';
        const res = await api.get(`/admin/demo-director/angle-recommendation${qs}`);
        if (!cancelled) setRec(res.data);
      } catch (_e) { /* additive — never block the page */ }
    };
    run();
    return () => { cancelled = true; };
  }, [industryPreset]);
  return rec;
};

/**
 * Tiny "✨ Recommended" / "Tentative" / "Suggested" pill rendered
 * next to a chosen angle. Hover surfaces a multi-line "why" tooltip
 * with raw conversion %, smoothed %, sends, and a confidence tier.
 *
 * Three visual tiers, driven by `rec.source`:
 *   • 'data'       → emerald "Recommended" — angle has ≥3 in-scope
 *                    sends and was the top Wilson-ranked option
 *   • 'tentative'  → slate "Tentative" — angle is a data winner
 *                    but sample is too small to crown (1–2 sends)
 *   • 'complement' → slate "Suggested" — curated pairing with no
 *                    historical data (we only have one data winner)
 */
export const RecommendedPill = ({ rec, scope, testId }) => {
  if (!rec) return null;
  const isData = rec.source === 'data';
  const isTentative = rec.source === 'tentative';
  const cls = isData
    ? 'bg-emerald-100 text-emerald-700 ring-emerald-200'
    : 'bg-slate-100 text-slate-600 ring-slate-200';
  const label = isData ? 'Recommended' : isTentative ? 'Tentative' : 'Suggested';
  const confLabel = (
    rec.confidence === 'high' ? 'High'
    : rec.confidence === 'medium' ? 'Medium'
    : rec.confidence === 'low' ? 'Low'
    : 'Tiny'
  );
  let tooltip;
  if (rec.source === 'complement') {
    tooltip = 'Curated complement to the data winner — no historical data yet';
  } else {
    const rawPct = rec.conversion_pct == null ? '—' : `${rec.conversion_pct}%`;
    const smoothPct = rec.smoothed_pct == null ? '—' : `${rec.smoothed_pct}%`;
    const wilson = rec.wilson_lower == null ? '—' : `${rec.wilson_lower}%`;
    tooltip = (
      `${rec.sends} send${rec.sends === 1 ? '' : 's'} · ${confLabel} confidence\n` +
      `Raw return: ${rawPct} · Smoothed: ${smoothPct} · Wilson 95% LB: ${wilson}\n` +
      `Scope: ${scope || 'global'}`
    );
  }
  return (
    <span
      title={tooltip}
      className={`inline-flex items-center gap-0.5 text-[8px] font-bold uppercase tracking-wider ring-1 rounded-full px-1.5 py-px ${cls}`}
      data-testid={testId}
      data-rec-source={rec.source}
      data-rec-confidence={rec.confidence}
    >
      <Sparkles className="h-2.5 w-2.5" /> {label}
    </span>
  );
};
